import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import PlanPriceDisplay from './PlanPriceDisplay';
import { formatPrice } from './PlanPricingTabs';
import {
  BILLING_CYCLES,
  cycleSuffix,
  pricingForCycle,
} from '../../utils/planPricing';

const CYCLE_BADGES = {
  half_yearly: { label: 'Recommended', className: 'bg-blue-600 text-white' },
  yearly: { label: 'Best Value', className: 'bg-green-600 text-white' },
};

const ACTION_LABELS = {
  subscribe: 'Choose Plan',
  upgrade: 'Choose Plan',
  renew: 'Choose Plan',
};

/**
 * Side-by-side billing cycle comparison for a single plan.
 * @param {object} props
 * @param {object} props.plan
 * @param {'subscribe'|'upgrade'|'renew'} [props.action]
 * @param {(cycle: string) => void} props.onSelectCycle
 * @param {() => void} [props.onCancel]
 * @param {boolean} [props.showHeader]
 * @param {string} [props.selectedCycle]
 */
export default function BillingCycleComparison({
  plan,
  action = 'subscribe',
  onSelectCycle,
  onCancel,
  showHeader = true,
  selectedCycle,
}) {
  if (!plan) return null;

  const currency = plan.currency || 'INR';
  const chooseLabel = ACTION_LABELS[action] || 'Choose Plan';

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">{plan.name}</h3>
            <p className="text-sm text-muted-foreground">
              Compare billing options and choose the best value for your school
            </p>
          </div>
          {onCancel && (
            <Button variant="ghost" size="sm" onClick={onCancel} className="shrink-0">
              Cancel
            </Button>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-3">
        {BILLING_CYCLES.map(({ key, label }) => {
          const pricing = pricingForCycle(plan, key);
          const badge = CYCLE_BADGES[key];
          const isHighlighted = key === 'half_yearly' || key === 'yearly';
          const isSelected = selectedCycle === key;

          return (
            <Card
              key={key}
              className={`flex flex-col h-full ${
                isSelected
                  ? 'border-blue-600 ring-2 ring-blue-600/30 shadow-md'
                  : isHighlighted
                    ? 'border-blue-500/60 shadow-sm ring-1 ring-blue-500/10'
                    : ''
              }`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-base">{label}</CardTitle>
                  {badge && (
                    <Badge className={`shrink-0 text-xs ${badge.className}`}>{badge.label}</Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground capitalize">
                  {key.replace(/_/g, ' ')} billing{cycleSuffix(key)}
                </p>
              </CardHeader>
              <CardContent className="flex flex-col flex-1 space-y-3">
                <PlanPriceDisplay plan={plan} cycle={key} size="sm" showCycleSuffix={key === 'monthly'} />

                {pricing.save_extra > 0 && (
                  <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                    Save Extra {formatPrice(pricing.save_extra, currency)}
                  </p>
                )}

                <div className="flex-1" />

                <Button
                  className={`w-full ${isSelected ? '' : 'bg-blue-600 hover:bg-blue-700'}`}
                  variant={isSelected ? 'secondary' : 'default'}
                  onClick={() => onSelectCycle(key)}
                >
                  {isSelected ? 'Selected' : chooseLabel}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
